import {
  withDriveMetadata,
  findKbDocByDriveFileId,
  type GoogleDriveKbMetadata,
} from "@/lib/googleDriveKbLink";
import {
  createDriveFolderMirror,
  extractFolderPathsFromDrivePaths,
} from "@/lib/googleDriveFolderMirror";
import { getThoughtProcessPreferences } from "@/lib/thoughtProcessPreferences";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import {
  getDriveFileBlob,
  type DriveEntry,
} from "@/services/googleDriveService";
import { ragService } from "@/services/ragService";
import { extractTextFromBlob } from "@/utils/textExtractionHelper";
import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import type { LibraryFolder } from "@/services/foldersService";

export type DriveImportFile = DriveEntry & {
  drivePath?: string;
};

export type DriveImportOptions = {
  token: string;
  workspaceId?: string | null;
  spaceId?: string | null;
  targetFolderId?: string | null;
  mirrorFolders?: boolean;
  includeInRag?: boolean;
  embedAfterImport?: boolean;
  updateExisting?: boolean;
  kbItems?: KnowledgeBaseItem[];
  existingFolders?: LibraryFolder[];
  onProgress?: (current: string, completed: number, total: number) => void;
};

export type DriveImportResult = {
  created: string[];
  updated: string[];
  failed: Array<{ name: string; error: string }>;
  skipped: string[];
};

function resolveFolderIdForFile(
  drivePath: string | undefined,
  folderMap: Map<string, string>,
  targetFolderId: string | null
): string | null {
  if (!drivePath?.trim()) return targetFolderId;
  return folderMap.get(drivePath.trim()) ?? targetFolderId;
}

export async function importDriveFiles(
  files: DriveImportFile[],
  options: DriveImportOptions
): Promise<DriveImportResult> {
  const {
    token,
    workspaceId = null,
    spaceId = null,
    targetFolderId = null,
    mirrorFolders = false,
    includeInRag = true,
    embedAfterImport = getThoughtProcessPreferences().autoEmbedDocuments,
    updateExisting = true,
    kbItems = [],
    existingFolders,
    onProgress,
  } = options;

  const result: DriveImportResult = {
    created: [],
    updated: [],
    failed: [],
    skipped: [],
  };

  let folderMap = new Map<string, string>();
  if (mirrorFolders) {
    const paths = extractFolderPathsFromDrivePaths(
      files.map((f) => f.drivePath)
    );
    if (paths.length > 0) {
      folderMap = await createDriveFolderMirror(
        paths,
        workspaceId ?? null,
        existingFolders
      );
    }
  }

  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onProgress?.(file.name, i, total);

    const existing =
      updateExisting && kbItems.length > 0
        ? findKbDocByDriveFileId(kbItems, file.id)
        : undefined;

    try {
      const blob = await getDriveFileBlob(file, token, true);
      const extracted = await extractTextFromBlob(
        blob,
        file.name,
        file.mimeType,
        token
      );

      if (extracted.error || !extracted.text?.trim()) {
        result.failed.push({
          name: file.name,
          error: extracted.error || "No extractable text",
        });
        continue;
      }

      const driveMeta: GoogleDriveKbMetadata = {
        fileId: file.id,
        folderId: file.parents?.[0],
        modifiedTime: file.modifiedTime,
        mimeType: file.mimeType,
        url: file.webViewLink,
        importedAt: new Date().toISOString(),
        drivePath: file.drivePath,
      };

      const folderId = resolveFolderIdForFile(
        file.drivePath,
        folderMap,
        targetFolderId
      );

      if (existing) {
        const metadata = withDriveMetadata(existing.metadata, driveMeta);
        await knowledgeBaseService.updateKnowledgeBaseItem(existing.id, {
          title: file.name,
          content: extracted.text,
          metadata,
          includeInRag,
        });
        await ragService.reembedDocument(existing.id);
        result.updated.push(existing.id);
      } else {
        const item = await knowledgeBaseService.createKnowledgeBaseItem(
          {
            title: file.name,
            content: extracted.text,
            type: "note",
            spaceId,
            folderId,
            includeInRag,
            metadata: withDriveMetadata({}, driveMeta),
          },
          workspaceId ?? null
        );

        if (!embedAfterImport) {
          ragService.cancelScheduledEmbedDocument(item.id);
        } else if (!getThoughtProcessPreferences().autoEmbedDocuments) {
          ragService.scheduleEmbedDocument(item.id);
        }

        result.created.push(item.id);
      }
    } catch (e) {
      result.failed.push({
        name: file.name,
        error: e instanceof Error ? e.message : "Import failed",
      });
    }
  }

  onProgress?.("Complete", total, total);
  return result;
}
